import React, { useState, useCallback } from "react";

import {
  useDefaultCountryId,
  useDefaultStateId
} from "lib/config";

import {
  Grid,
  DialogContent as MuiDialogContent,
  Button,
  Tabs,
  Tab,
  Box,
  LinearProgress,
  Select,
  InputLabel,
  MenuItem,
} from "@material-ui/core";

import { useTranslation } from "react-i18next";

import CountrySelector from "components/CountrySelector";
import StateSelector from "components/StateSelector";
import CountySelector from "components/CountySelector";
import DateSelector from "components/DateSelector";

import { withStyles } from "@material-ui/core/styles";

import { useStateWithDeps } from "lib/hooks";

import {
  DialogActions,
  Dialog as CloseableDialog,
  DialogTitle,
} from "components/CloseableDialog";

// import { getDefaultLevel1ForCountry } from "lib/countries";

const DialogContent = withStyles((theme) => ({
  root: {
    padding: theme.spacing(0),
  },
}))(MuiDialogContent);

const StyledTabs = withStyles((theme) => ({
  root: {
    // borderRight: `1px solid ${theme.palette.divider}`,
    paddingRight: theme.spacing(2),
  },
}))(Tabs);

const Dialog = withStyles({
  paper: {
    maxWidth: 700,
  },
})(CloseableDialog);

const StyledTab = withStyles((theme) => ({
  selected: {},
  textColorPrimary: {
    "&$selected": {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
    },
  },
}))(Tab);

function DivisionSelector({ onChange, value, label, disabled }) {
  const { t } = useTranslation();

  const handleChange = (event) => {
    onChange(event.target.value);
  };

  return (
    <div>
      <InputLabel shrink id="division-select-label">
        {label}
      </InputLabel>
      <Select
        disabled={disabled}
        labelId="division-select-label"
        value={value}
        onChange={handleChange}
        style={{ width: "100%" }}
      >
        <MenuItem value="polygons">{t("divisionSelector.polygons")}</MenuItem>
        <MenuItem value="hex-small">{t("divisionSelector.hexSmall")}</MenuItem>
        <MenuItem value="hex-medium">
          {t("divisionSelector.hexMedium")}
        </MenuItem>
        <MenuItem value="hex-large">{t("divisionSelector.hexLarge")}</MenuItem>
      </Select>
    </div>
  );
}

const WORLD_TAB = 0;
const COUNTRY_TAB = 1;
const STATE_TAB = 2;
const COUNTY_TAB = 3;

function getLevelFromTabNumber(tab) {
  return tab;
}

function Content({
  country,
  onCountryChange,
  state,
  onStateChange,
  county,
  onCountyChange,
  baselineDate,
  onBaselineDateChange,
  startDate,
  onStartDateChange,
  endDate,
  onEndDateChange,
  minDate,
  maxDate,
  tab,
  countries,
  division,
  onChangeDivision,
}) {
  const { t } = useTranslation();

  return (
    <>
      <Grid container spacing={3}>
        <Grid item md={4} xs={12}>
          <DateSelector
            label={t("baselineDate.label")}
            date={baselineDate}
            onDateChange={onBaselineDateChange}
            minDate={minDate}
            maxDate={maxDate}
          />
        </Grid>
        <Grid item md={4} xs={12}>
          {/* <DateRangeSelector label={t("download.dateRange.label")} /> */}
          <DateSelector
            label={t("download.startDate")}
            date={startDate}
            onDateChange={onStartDateChange}
            minDate={minDate}
            maxDate={endDate}
          />
        </Grid>
        <Grid item md={4} xs={12}>
          {/* <DateRangeSelector label={t("download.dateRange.label")} /> */}
          <DateSelector
            label={t("download.endDate")}
            date={endDate}
            onDateChange={onEndDateChange}
            minDate={startDate}
            maxDate={maxDate}
          />
        </Grid>

        <Grid item md={4} xs={12}>
          <CountrySelector
            allowedCountries={countries}
            label={t("countrySelector.label")}
            country={country}
            onCountryChange={onCountryChange}
            disabled={tab < COUNTRY_TAB}
          />
        </Grid>
        <Grid item md={4} xs={12}>
          <StateSelector
            label={t("stateSelector.label")}
            state={state}
            country={country}
            onStateChange={onStateChange}
            disabled={tab < STATE_TAB}
          />
        </Grid>
        {/* <Grid item md={4} xs={12}>
          <CountySelector
            label={t("countySelector.label")}
            state={state}
            country={country}
            county={county}
            onCountyChange={onCountyChange}
            disabled={tab !== COUNTY_TAB}
            extraValues={[{ id: "all", name: t("download.all") }]}
          />
        </Grid> */}
        <Grid item md={4} xs={12}>
          <DivisionSelector
            label={t("download.divisionSelector")}
            value={division}
            onChange={onChangeDivision}
            disabled={tab !== STATE_TAB}
          />
        </Grid>
      </Grid>
    </>
  );
}
export default function PureDownloadDialog({
  onDownload,
  open,
  onClose,
  minDate,
  maxDate,
  pending,
  defaultCountry,
  defaultDate,
  defaultBaslineDate,
  countries = [],
}) {
  // const availableCountries = useAvailableCountries();

  // const defaultCountryId = useMemo(() => {
  //   if (defaultCountry) {
  //     return defaultCountry;
  //   }
  //   if (countries === "all") {
  //     return availableCountries[0].id;
  //   }

  //   return availableCountries.find(({ id }) => countries.includes(id))?.id;
  // }, [defaultCountry, countries, availableCountries]);

  const defaultCountryId = useDefaultCountryId();

  const [division, setDivision] = useState("polygons");

  const [country, setCountry] = useState(defaultCountryId);

  const defaultStateId = useDefaultStateId(country);

  const [state, setState] = useStateWithDeps(defaultStateId, [country]);

  const [county, setCounty] = useStateWithDeps("all", [country, state]);

  const [baselineDate, setBaselineDate] = useState(minDate);
  const [startDate, setStartDate] = useState(maxDate);
  const [endDate, setEndDate] = useState(maxDate);

  const [tab, setTab] = useState(COUNTRY_TAB);

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
  };

  const handleCountryChange = useCallback(
    (newCountry) => {
      setCountry(newCountry);
      setState(defaultStateId);
    },
    [setCountry, setState, defaultStateId]
  );

  const HEX_SCALES = {
    "hex-small": 8,
    "hex-medium": 7,
    "hex-large": 6,
  };

  function getHexCode(division) {
    return HEX_SCALES[division];
  }

  const handleDownload = () => {
    onDownload({
      level: getLevelFromTabNumber(tab),
      country,
      county,
      state,
      startDate,
      endDate,
      baselineDate,
      hexagons: division !== "polygons" ? getHexCode(division) : undefined,
    });
  };

  const { t } = useTranslation();

  return (
    <Dialog
      fullWidth
      open={open}
      onClose={onClose}
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle id="login-dialog-title" onClose={onClose}>
        {t("download.title")}
      </DialogTitle>

      <DialogContent>
        <Box display="flex">
          <Box>
            <StyledTabs
              orientation="vertical"
              value={tab}
              onChange={handleTabChange}
              indicatorColor="secondary"
              textColor="primary"
            >
              {countries === "all" && (
                <StyledTab
                  value={WORLD_TAB}
                  label={t("download.tabs.world")}
                  id="tab-world"
                  aria-controls="tab-world"
                />
              )}
              <StyledTab
                value={COUNTRY_TAB}
                label={t("download.tabs.country")}
                id="tab-country"
                aria-controls="tab-country"
              />
              <StyledTab
                value={STATE_TAB}
                label={t("download.tabs.state")}
                id="tab-state"
                aria-controls="tab-state"
              />
              <StyledTab
                value={COUNTY_TAB}
                label={t("download.tabs.county")}
                id="tab-county"
                aria-controls="tab-county"
              />
            </StyledTabs>
          </Box>
          <Box p={2}>
            <Content
              tab={tab}
              country={country}
              onCountryChange={handleCountryChange}
              state={state}
              onStateChange={setState}
              county={county}
              onCountyChange={setCounty}
              baselineDate={baselineDate}
              onBaselineDateChange={setBaselineDate}
              startDate={startDate}
              onStartDateChange={setStartDate}
              endDate={endDate}
              onEndDateChange={setEndDate}
              minDate={minDate}
              maxDate={maxDate}
              countries={countries}
              division={division}
              onChangeDivision={setDivision}
            />
          </Box>
        </Box>

      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          {t("download.cancel")}
        </Button>
        <Button onClick={handleDownload} disabled={pending} color="primary">
          {t("download.confirm")}
        </Button>
      </DialogActions>
      {pending && <LinearProgress />}
    </Dialog>
  );
}
